/**
 * Resolves the canonical entity ID from a Visit-shaped wire payload.
 *
 * The Visit ID can appear in two locations depending on the API surface:
 *   1. `visit.visitId` (direct — the common case for WebSocket pushes)
 *   2. `visit.data.equipment.visitDetails.visitId.id` (nested — historical
 *      Datapath envelope format)
 *
 * Returns the first truthy, non-whitespace value found in priority order,
 * or `""` (empty string) when neither location carries a usable ID.
 * Callers should treat empty string as "missing" and skip/warn accordingly.
 *
 * This helper is a pure function with no side effects — safe for use in
 * reducers, selectors, and API seed paths. It exists to unify the
 * duplicated ID resolution that previously lived in `ingestVisit`,
 * `selectPillEnrichmentMap`, and the `getYardVisits` API seed matcher.
 */
import type { VersionedVisit } from "@/features/ryteTracker/store/ryteDataSlice";

type VisitIdentitySource = Pick<VersionedVisit, "visitId" | "data">;

export const resolveVisitEntityId = (visit: VisitIdentitySource): string => {
  const direct = visit.visitId;
  if (direct && direct.trim()) return direct;

  const nested = visit.data?.equipment?.visitDetails?.visitId?.id;
  if (nested && nested.trim()) return nested;

  return "";
};
